import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  styled,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  CircularProgress,
  Chip,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Collapse,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
  backgroundColor:
    mode === "dark"
      ? "#2A2A3C"
      : mode === "custom"
        ? customColors?.background || "#f5f5f5"
        : "#f0f7ff",
  borderRadius: "16px",
  boxShadow:
    mode === "dark"
      ? "0 8px 24px rgba(0, 0, 0, 0.3)"
      : "0 8px 24px rgba(30, 139, 175, 0.12)",
}));

// Palette setup
const palette = {
  light: {
    headerBg: "#e3f2fd",
    headerText: "#37474f",
    rowHover: "#f5f5f5",
    text: "#333",
  },
  dark: {
    headerBg: "#2A2A3C",
    headerText: "#EDEDED",
    rowHover: "#383C54",
    text: "#EDEDED",
  },
  custom: (customColors) => ({
    headerBg: customColors?.cardBg || "#f5f5f5",
    headerText: customColors?.text || "#111111",
    rowHover: customColors?.primary || "#e8f4ff",
    text: customColors?.text || "#111111",
  }),
};

export default function ManageFoldersPanel({
  mode,
  customColors,
  setSnackbar,
  isLoading,
  setIsLoading,
  currentUserRole,
}) {
  const [activeAssignTab, setActiveAssignTab] = useState("free");
  const [selectedUnassignFolders, setSelectedUnassignFolders] = useState({});
  const [openUnassignModal, setOpenUnassignModal] = useState(false);
  const [assignedFolders, setAssignedFolders] = useState([]);
  const [classes, setClasses] = useState([]);
  const [folders, setFolders] = useState([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedFolders, setSelectedFolders] = useState({}); // Changed to object: { folderId: { selected: true, accessType: 'free' } }
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [isLoadingUnassignFolders, setIsLoadingUnassignFolders] = useState(false); // New state for unassign modal loader
  const [tableSearchTerm, setTableSearchTerm] = useState(""); // New state for table search
  const [currentPage, setCurrentPage] = useState(1); // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(5); // Rows per page state
  const [unassignSearchTerm, setUnassignSearchTerm] = useState(""); // Search term for unassign modal
  const [filterType, setFilterType] = useState("all"); // Filter type for unassign modal: "all", "free", "paid"
  const [filterAnchorEl, setFilterAnchorEl] = useState(null); // For filter dropdown

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    setSelectedFolders((prev) => {
      const updatedSelected = { ...prev };
      Object.keys(updatedSelected).forEach((folderId) => {
        if (!updatedSelected[folderId].disabled) {
          updatedSelected[folderId].accessType = activeAssignTab;
        }
      });
      return updatedSelected;
    });
  }, [activeAssignTab]);

  // Fetch classes from API
  useEffect(() => {
    const fetchClasses = async () => {
      setIsLoading(true);
      try {
        const token = sessionStorage.getItem("authToken");
        const response = await fetch(`${apiUrl}/admins/view-student-classes/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setClasses(Array.isArray(data) ? data : []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to fetch classes.",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchClasses();
  }, [apiUrl]);

  // Fetch folders from API
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const token = sessionStorage.getItem("authToken");
        const response = await fetch(`${apiUrl}/folders/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        // Extract folders from own_folders array
        setFolders(Array.isArray(data.own_folders) ? data.own_folders : []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to fetch folders.",
          severity: "error",
        });
      }
    };
    fetchFolders();
  }, [apiUrl]);

  // Filter classes based on table search term
  const filteredClasses = classes.filter(cls =>
    cls.name.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
    cls.description.toLowerCase().includes(tableSearchTerm.toLowerCase())
  );

  // Calculate pagination
  const indexOfLastRow = Math.min(currentPage * rowsPerPage, filteredClasses.length);
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage;
  const currentClasses = filteredClasses.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredClasses.length / rowsPerPage);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  // Handle table search
  const handleTableSearchChange = (e) => {
    setTableSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle assign modal open
  const handleOpenAssignModal = async (classItem) => {
    setSelectedClass(classItem);
    setOpenAssignModal(true);
    setIsLoading(true);
    try {
      const assignedFolders = await fetchAssignedFoldersForClass(classItem);
      const initialSelectedFolders = {};
      assignedFolders.forEach((folder) => {
        initialSelectedFolders[folder.id] = { accessType: folder.accessType, disabled: true };
      });
      setSelectedFolders(initialSelectedFolders);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to open assign modal.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignedFoldersForClass = async (classItem) => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(`${apiUrl}/admins/class-student-folder-overview/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      const selectedClassData = data.find((item) => item.class_name === classItem.name);

      const allAssignedFolders = [
        ...(selectedClassData?.free_class_folders || []).map((folder) => ({ ...folder, accessType: "free" })),
        ...(selectedClassData?.paid_class_folders || []).map((folder) => ({ ...folder, accessType: "paid" })),
      ];

      return allAssignedFolders;
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to fetch assigned folders.",
        severity: "error",
      });
      return [];
    }
  };

  const handleOpenUnassignModal = async (classItem) => {
    setSelectedClass(classItem);
    setOpenUnassignModal(true);
    setIsLoadingUnassignFolders(true); // Use the new loader state
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(`${apiUrl}/admins/class-student-folder-overview/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      // Find the selected class in the response
      const selectedClassData = data.find((item) => item.class_name === classItem.name);

      // Combine free and paid folders for the selected class
      const allAssignedFolders = [
        ...(selectedClassData?.free_class_folders || []).map((folder) => ({ ...folder, accessType: "free" })),
        ...(selectedClassData?.paid_class_folders || []).map((folder) => ({ ...folder, accessType: "paid" })),
      ];

      setAssignedFolders(allAssignedFolders);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to fetch assigned folders.",
        severity: "error",
      });
    } finally {
      setIsLoadingUnassignFolders(false); // Stop the loader
    }
  };

  const handleCloseUnassignModal = () => {
    setOpenUnassignModal(false);
    setSelectedClass(null);
    setAssignedFolders([]);
    setSelectedUnassignFolders({});
    setUnassignSearchTerm(""); // Reset search term
    setFilterType("all"); // Reset filter
    setFilterAnchorEl(null); // Close filter dropdown
  };

  const handleUnassignFolders = async () => {
    const selectedFolderIds = Object.keys(selectedUnassignFolders).filter(
      (folderId) => selectedUnassignFolders[folderId]
    );

    if (selectedFolderIds.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one folder to unassign.",
        severity: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(`${apiUrl}/admins/folders/unassign/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder_ids: selectedFolderIds,
          student_class: selectedClass.name,
          include_subfolders: true,
        }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `Successfully unassigned ${selectedFolderIds.length} folder(s) from ${selectedClass.name}.`,
          severity: "success",
        });
        handleCloseUnassignModal();
        setSelectedUnassignFolders({});
      } else {
        throw new Error("Failed to unassign folders");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to unassign folders. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle assign modal close
  const handleCloseAssignModal = () => {
    setOpenAssignModal(false);
    setSelectedClass(null);
    setSelectedFolders({});
    setSearchTerm("");
  };

  // Handle folder expansion toggle
  const handleToggleExpand = (folderId) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  // Handle folder selection toggle
  const handleFolderToggle = (folderId) => {
    setSelectedFolders((prev) => {
      const newSelected = { ...prev };
      if (newSelected[folderId]) {
        delete newSelected[folderId];
      } else {
        newSelected[folderId] = { accessType: activeAssignTab };
      }
      return newSelected;
    });
  };

  // Handle access type change
  const handleAccessTypeChange = (folderId, accessType) => {
    setSelectedFolders((prev) => ({
      ...prev,
      [folderId]: { ...prev[folderId], accessType },
    }));
  };

  // Handle assign folders submission
  const handleAssignFolders = async () => {
    // Filter out disabled folders and get only the newly selected folders
    const selectedFolderIds = Object.keys(selectedFolders).filter(
      (folderId) => !selectedFolders[folderId].disabled
    );

    if (selectedFolderIds.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one folder.",
        severity: "warning",
      });
      return;
    }

    // Check if all selected folders have the same access type
    const accessTypes = selectedFolderIds.map((folderId) => selectedFolders[folderId].accessType);
    const allSameAccessType = accessTypes.every((type) => type === accessTypes[0]);

    if (!allSameAccessType) {
      setSnackbar({
        open: true,
        message: "All selected folders must have the same access type (either all Free or all Paid).",
        severity: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("authToken");

      // Get access type from the first selected folder
      const accessType = selectedFolders[selectedFolderIds[0]].accessType;

      const response = await fetch(`${apiUrl}/admins/folders/assign/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder_id: selectedFolderIds,
          student_class: selectedClass.name,
          access_type: accessType,
        }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `Successfully assigned ${selectedFolderIds.length} folder(s) to ${selectedClass.name}.`,
          severity: "success",
        });
        handleCloseAssignModal();
      } else {
        throw new Error("Failed to assign folders");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to assign folders. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Flatten all folders including subfolders for search
  const flattenFolders = (folders) => {
    let flatList = [];
    folders.forEach((folder) => {
      flatList.push(folder);
      if (folder.subfolders && folder.subfolders.length > 0) {
        flatList = flatList.concat(flattenFolders(folder.subfolders));
      }
    });
    return flatList;
  };

  // Filter folders based on search term - only show main folders (type === 'folder')
  const filteredFolders = folders
    .filter((folder) => folder.type === "folder") // Only main folders
    .filter((folder) => {
      const matchesSearch = folder.name?.toLowerCase().includes(searchTerm.toLowerCase());
      // If parent folder matches, show it
      if (matchesSearch) return true;
      // If any subfolder matches, show parent too
      if (folder.subfolders && folder.subfolders.length > 0) {
        const flatSubfolders = flattenFolders(folder.subfolders);
        return flatSubfolders.some((sub) =>
          sub.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      return false;
    });

  // Filter assigned folders for unassign modal
  const filteredAssignedFolders = assignedFolders.filter((folder) => {
    // Search filter
    const matchesSearch = folder.name?.toLowerCase().includes(unassignSearchTerm.toLowerCase());

    // Type filter
    const matchesType =
      filterType === "all" ||
      (filterType === "free" && folder.accessType === "free") ||
      (filterType === "paid" && folder.accessType === "paid");

    return matchesSearch && matchesType;
  });

  // Handle filter dropdown open
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  // Handle filter dropdown close
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Handle filter selection
  const handleFilterSelect = (type) => {
    setFilterType(type);
    handleFilterClose();
  };

  // Get filter display text
  const getFilterDisplayText = () => {
    switch (filterType) {
      case "free":
        return "Free Folder";
      case "paid":
        return "Paid Folder";
      default:
        return "All Types";
    }
  };

  // Recursive component to render folder with subfolders
  const FolderItem = ({ folder, level = 0 }) => {
    const isSelected = selectedFolders[folder.id];
    const isDisabled = isSelected?.disabled;

    return (
      <Box key={folder.id}>
        <Box
          sx={{
            p: 2,
            pl: 2 + level * 3,
            borderRadius: "12px",
            backgroundColor:
              mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(30, 139, 175, 0.05)",
            border:
              mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(30, 139, 175, 0.1)",
            mb: 1,
            opacity: isDisabled ? 0.6 : 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: isDisabled
                ? "transparent"
                : mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(30, 139, 175, 0.08)",
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={!!isSelected}
              disabled={isDisabled}
              onChange={() => !isDisabled && handleFolderToggle(folder.id)}
              sx={{
                color: isDisabled ? "rgba(0, 0, 0, 0.26)" : "#1e8bafff",
                "&.Mui-checked": {
                  color: isDisabled ? "rgba(0, 0, 0, 0.26)" : "#1e8bafff",
                },
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            />
            <FolderIcon sx={{ color: isDisabled ? "rgba(0, 0, 0, 0.26)" : "#1e8bafff", fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color:
                    mode === "dark"
                      ? palette.dark.text
                      : mode === "custom"
                        ? customColors?.text || "#1e3a8a"
                        : isDisabled ? "rgba(0, 0, 0, 0.38)" : "#1e3a8a",
                }}
              >
                {folder.name}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  };

  return (
    <StyledPaper
      mode={mode}
      customColors={customColors}
      sx={{
        backgroundColor:
          mode === "dark"
            ? palette.dark.headerBg
            : mode === "custom"
              ? customColors?.bgNav || "#f5f5f5"
              : palette.light.headerBg,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          mb: 3,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          pb: 2,
          borderBottom:
            mode === "dark"
              ? "2px solid rgba(255, 255, 255, 0.1)"
              : "2px solid rgba(30, 139, 175, 0.15)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontWeight: 700,
            letterSpacing: "-0.5px",
          }}
        >
          Manage Folders
        </Typography>

        {/* Search Bar */}
        <TextField
          placeholder="Search classes..."
          variant="outlined"
          size="small"
          value={tableSearchTerm}
          onChange={handleTableSearchChange}
          InputProps={{
            startAdornment: <SearchIcon sx={{
              mr: 1,
              color: mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"
            }} />,
          }}
          sx={{
            width: 250,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
              '&:hover': {
                backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(30, 139, 175, 0.08)",
              },
              '&.Mui-focused': {
                backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(30, 139, 175, 0.1)",
                boxShadow: "0 0 0 2px rgba(30, 139, 175, 0.2)",
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(30, 139, 175, 0.3)",
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.4)" : "#1e8bafff",
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: "#1e8bafff",
            },
          }}
        />
      </Stack>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#1e8bafff" }} size={48} />
        </Box>
      ) : filteredClasses.length > 0 ? (
        <>
          <TableContainer
            sx={{
              width: "100%",
              borderRadius: "12px",
              overflow: "hidden",
              border:
                mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(30, 139, 175, 0.15)",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                  }}
                >
                  <TableCell
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      py: 2,
                      textTransform: "uppercase",
                    }}
                  >
                    Class Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      py: 2,
                      textTransform: "uppercase",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      py: 2,
                      textTransform: "uppercase",
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentClasses.map((cls, index) => (
                  <TableRow
                    key={cls.id}
                    sx={{
                      backgroundColor:
                        mode === "dark"
                          ? index % 2 === 0
                            ? "rgba(255, 255, 255, 0.03)"
                            : "transparent"
                          : index % 2 === 0
                            ? "rgba(30, 139, 175, 0.03)"
                            : "transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          mode === "dark"
                            ? "rgba(30, 139, 175, 0.1)"
                            : "rgba(30, 139, 175, 0.08)",
                        transform: "scale(1.01)",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            mode === "dark"
                              ? palette.dark.text
                              : mode === "custom"
                                ? customColors?.text || "#1e3a8a"
                                : "#1e3a8a",
                          fontWeight: 600,
                          fontSize: "0.95rem",
                        }}
                      >
                        {cls.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            mode === "dark"
                              ? "rgba(255, 255, 255, 0.7)"
                              : "rgba(0, 0, 0, 0.7)",
                          fontSize: "0.9rem",
                        }}
                      >
                        {cls.description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Tooltip title="Assign Folders" arrow>
                        <Button
                          startIcon={<AssignmentIcon />}
                          variant="contained"
                          onClick={() => handleOpenAssignModal(cls)}
                          sx={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                            color: "white",
                            borderRadius: 2,
                            fontWeight: 600,
                            px: 2.5,
                            py: 0.75,
                            mr: 1,
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(30, 58, 138, 0.3)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: "linear-gradient(135deg, #1a3278 0%, #1a7a9a 100%)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 16px rgba(30, 58, 138, 0.4)",
                            },
                          }}
                        >
                          Assign
                        </Button>
                      </Tooltip>
                      <Tooltip title="Unassign Folders" arrow>
                        <Button
                          onClick={() => handleOpenUnassignModal(cls)}
                          startIcon={<AssignmentReturnIcon />}
                          variant="contained"
                          sx={{
                            background: "linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)",
                            color: "white",
                            borderRadius: 2,
                            fontWeight: 600,
                            px: 2.5,
                            py: 0.75,
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(185, 28, 28, 0.35)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: "linear-gradient(135deg, #991b1b 0%, #dc2626 100%)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 18px rgba(185, 28, 28, 0.5)",
                            },
                          }}
                        >
                          Unassign
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls with Size Selector */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 4,
            flexWrap: 'wrap',
            gap: 2
          }}>
            {/* Page Info */}
            <Typography sx={{
              color: mode === 'dark' ? '#EDEDED' : '#37474f',
              fontWeight: 500,
              fontSize: '0.9rem',
            }}>
              Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredClasses.length)} of {filteredClasses.length} classes
            </Typography>

            {/* Pagination Controls */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}>
              {/* Rows per page selector */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e8bafff',
                    },
                    '& .MuiSelect-select': {
                      py: 0.75,
                      px: 2,
                    },
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(30, 139, 175, 0.05)',
                  }}
                >
                  <MenuItem value={5}>5 per page</MenuItem>
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={15}>15 per page</MenuItem>
                  <MenuItem value={20}>20 per page</MenuItem>
                  <MenuItem value={25}>25 per page</MenuItem>
                </Select>
              </FormControl>

              {/* Prev/Next buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  startIcon={<ArrowBackIosIcon sx={{ fontSize: '0.9rem' }} />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 3,
                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    color: mode === 'dark' ? '#EDEDED' : '#37474f',
                    minWidth: 90,
                    '&:hover': {
                      borderColor: '#1e8bafff',
                      backgroundColor: mode === 'dark' ? 'rgba(30, 139, 175, 0.1)' : 'rgba(30, 139, 175, 0.05)',
                    },
                    '&.Mui-disabled': {
                      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  Prev
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  endIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.9rem' }} />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 3,
                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    color: mode === 'dark' ? '#EDEDED' : '#37474f',
                    minWidth: 90,
                    '&:hover': {
                      borderColor: '#1e8bafff',
                      backgroundColor: mode === 'dark' ? 'rgba(30, 139, 175, 0.1)' : 'rgba(30, 139, 175, 0.05)',
                    },
                    '&.Mui-disabled': {
                      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            opacity: 0.7,
          }}
        >
          <FolderIcon sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }} />
          <Typography
            variant="h6"
            sx={{
              color: mode === "dark" ? palette.dark.text : "#37474f",
              fontWeight: 500,
            }}
          >
            {tableSearchTerm ? "No matching classes found" : "No classes found"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
              mt: 1,
            }}
          >
            {tableSearchTerm ? "Try a different search term" : "Classes need to be created before assigning folders"}
          </Typography>
        </Box>
      )}

      {/* Modal for Assigning Folders */}
      <Modal
        open={openAssignModal}
        onClose={handleCloseAssignModal}
        aria-labelledby="assign-folders-modal-title"
        aria-describedby="assign-folders-modal-description"
        sx={{
          backdropFilter: "blur(4px)",
          zIndex: 1500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 600 },
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: "20px",
            boxShadow:
              mode === "dark"
                ? "0 20px 60px rgba(0, 0, 0, 0.5)"
                : "0 20px 60px rgba(30, 58, 138, 0.15)",
            border:
              mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(30, 139, 175, 0.1)",
            background:
              mode === "dark"
                ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                : mode === "custom"
                  ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                  : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
            overflow: "hidden",
          }}
        >
          {/* Sticky Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              background:
                mode === "dark"
                  ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                  : mode === "custom"
                    ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                    : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
              p: 4,
              pb: 2,
              borderRadius: "20px 20px 0 0",
              boxShadow:
                mode === "dark"
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(30, 139, 175, 0.1)",
            }}
          >
            <Typography
              id="assign-folders-modal-title"
              variant="h5"
              sx={{
                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 1,
                fontWeight: 700,
                letterSpacing: "-0.5px",
              }}
            >
              Assign Folders to {selectedClass?.name}
            </Typography>

            {/* Tabs for Free/Paid Assignment */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                variant={activeAssignTab === "free" ? "contained" : "outlined"}
                onClick={() => setActiveAssignTab("free")}
                sx={{
                  background: activeAssignTab === "free" ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)" : "transparent",
                  color: activeAssignTab === "free" ? "white" : mode === "dark" ? "white" : "#1e3a8a",
                  borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(30, 139, 175, 0.3)",
                  borderRadius: "8px",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                }}
              >
                Free Assignment
              </Button>
              <Button
                variant={activeAssignTab === "paid" ? "contained" : "outlined"}
                onClick={() => setActiveAssignTab("paid")}
                sx={{
                  background: activeAssignTab === "paid" ? "linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)" : "transparent",
                  color: activeAssignTab === "paid" ? "white" : mode === "dark" ? "white" : "#b91c1c",
                  borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(185, 28, 28, 0.3)",
                  borderRadius: "8px",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                }}
              >
                Paid Assignment
              </Button>
            </Stack>

            <Typography
              id="assign-folders-modal-description"
              variant="body2"
              sx={{
                color:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : mode === "custom"
                      ? customColors?.text
                        ? `${customColors.text}99`
                        : "rgba(0, 0, 0, 0.6)"
                      : "rgba(0, 0, 0, 0.6)",
                fontSize: "0.875rem",
                mb: 2,
              }}
            >
              Select the folders you want to assign to this class
            </Typography>

            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search folders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "#1e8bafff" }} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor:
                    mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(30, 139, 175, 0.05)",
                },
              }}
            />
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 3,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(30, 139, 175, 0.3)",
                borderRadius: "4px",
              },
            }}
          >
            {filteredFolders.length > 0 ? (
              <Stack spacing={1.5}>
                {filteredFolders.map((folder) => (
                  <FolderItem key={folder.id} folder={folder} level={0} />
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  opacity: 0.7,
                }}
              >
                <FolderIcon
                  sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: mode === "dark" ? palette.dark.text : "#37474f",
                    fontWeight: 500,
                  }}
                >
                  {searchTerm ? "No folders found" : "No folders available"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      mode === "dark"
                        ? "rgba(255, 255, 255, 0.5)"
                        : "rgba(0, 0, 0, 0.5)",
                    mt: 1,
                  }}
                >
                  {searchTerm
                    ? "Try adjusting your search"
                    : "Create folders first to assign them"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Sticky Footer */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              background:
                mode === "dark"
                  ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                  : mode === "custom"
                    ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                    : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
              p: 2,
              borderRadius: "0 0 20px 20px",
              boxShadow:
                mode === "dark"
                  ? "0 -4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 -4px 12px rgba(30, 139, 175, 0.1)",
              borderTop:
                mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(30, 139, 175, 0.1)",
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  color:
                    mode === "dark"
                      ? "rgba(255, 255, 255, 0.7)"
                      : "rgba(0, 0, 0, 0.7)",
                  fontWeight: 500,
                }}
              >
                {Object.keys(selectedFolders).filter((folderId) => !selectedFolders[folderId].disabled).length} folder(s) selected
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  onClick={handleCloseAssignModal}
                  sx={{
                    color: mode === "dark" ? "#ffffff" : "#1e3a8a",
                    borderColor:
                      mode === "dark"
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(30, 139, 175, 0.3)",
                    textTransform: "none",
                    borderRadius: "12px",
                    fontWeight: 600,
                    px: 3,
                    py: 1.25,
                    "&:hover": {
                      borderColor:
                        mode === "dark"
                          ? "rgba(255, 255, 255, 0.5)"
                          : "rgba(30, 139, 175, 0.5)",
                      backgroundColor:
                        mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(30, 139, 175, 0.05)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignFolders}
                  variant="contained"
                  disabled={Object.keys(selectedFolders).filter((folderId) => !selectedFolders[folderId].disabled).length === 0}
                  sx={{
                    background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    px: 3,
                    py: 1.25,
                    borderRadius: "12px",
                    boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
                      boxShadow: "0 6px 20px rgba(30, 139, 175, 0.4)",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0px)",
                      boxShadow: "0 2px 8px rgba(30, 139, 175, 0.3)",
                    },
                    "&:disabled": {
                      background: "rgba(128, 128, 128, 0.3)",
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  }}
                >
                  Assign Folders
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* Modal for Unassigning Folders */}
      <Modal
        open={openUnassignModal}
        onClose={handleCloseUnassignModal}
        aria-labelledby="unassign-folders-modal-title"
        aria-describedby="unassign-folders-modal-description"
        sx={{
          backdropFilter: "blur(4px)",
          zIndex: 1500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 600 },
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: "20px",
            boxShadow:
              mode === "dark"
                ? "0 20px 60px rgba(0, 0, 0, 0.5)"
                : "0 20px 60px rgba(30, 58, 138, 0.15)",
            border:
              mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(30, 139, 175, 0.1)",
            background:
              mode === "dark"
                ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                : mode === "custom"
                  ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                  : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
            overflow: "hidden",
          }}
        >
          {/* Sticky Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              background:
                mode === "dark"
                  ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                  : mode === "custom"
                    ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                    : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
              p: 4,
              pb: 2,
              borderRadius: "20px 20px 0 0",
              boxShadow:
                mode === "dark"
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(30, 139, 175, 0.1)",
            }}
          >
            <Typography
              id="unassign-folders-modal-title"
              variant="h5"
              sx={{
                background: "linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 1,
                fontWeight: 700,
                letterSpacing: "-0.5px",
              }}
            >
              Unassign Folders from {selectedClass?.name}
            </Typography>
            <Typography
              id="unassign-folders-modal-description"
              variant="body2"
              sx={{
                color:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : mode === "custom"
                      ? customColors?.text
                        ? `${customColors.text}99`
                        : "rgba(0, 0, 0, 0.6)"
                      : "rgba(0, 0, 0, 0.6)",
                fontSize: "0.875rem",
                mb: 2,
              }}
            >
              These folders are currently assigned to this class
            </Typography>

            {/* Search Bar and Filter Dropdown Row */}
            <Stack direction="row" spacing={2} alignItems="center">
              {/* Search Bar */}
              <TextField
                fullWidth
                placeholder="Search folders..."
                value={unassignSearchTerm}
                onChange={(e) => setUnassignSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "#ef4444" }} />,
                }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor:
                      mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(239, 68, 68, 0.05)",
                  },
                }}
              />

              {/* Filter Dropdown Button */}
              <Button
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                onClick={handleFilterClick}
                sx={{
                  borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(239, 68, 68, 0.3)",
                  color: mode === "dark" ? "#ffffff" : "#ef4444",
                  textTransform: "none",
                  borderRadius: "12px",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  minWidth: 150,
                  "&:hover": {
                    borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "#ef4444",
                    backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(239, 68, 68, 0.05)",
                  },
                }}
              >
                Filter: {getFilterDisplayText()}
              </Button>

              {/* Filter Dropdown Menu */}
              <Box
                sx={{
                  position: "absolute",
                  right: 32,
                  top: 180,
                  zIndex: 9999,
                }}
              >
                <Paper
                  sx={{
                    width: 200,
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    display: filterAnchorEl ? "block" : "none",
                  }}
                >
                  <MenuItem
                    onClick={() => handleFilterSelect("all")}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontWeight: filterType === "all" ? 700 : 500,
                      backgroundColor: filterType === "all" ? "rgba(239, 68, 68, 0.1)" : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(239, 68, 68, 0.05)",
                      },
                    }}
                  >
                    All Types
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("free")}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontWeight: filterType === "free" ? 700 : 500,
                      backgroundColor: filterType === "free" ? "rgba(30, 139, 175, 0.1)" : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(30, 139, 175, 0.05)",
                      },
                    }}
                  >
                    Free Folder
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("paid")}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontWeight: filterType === "paid" ? 700 : 500,
                      backgroundColor: filterType === "paid" ? "rgba(239, 68, 68, 0.1)" : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(239, 68, 68, 0.05)",
                      },
                    }}
                  >
                    Paid Folder
                  </MenuItem>
                </Paper>
              </Box>
            </Stack>
          </Box>

          {/* Scrollable Content with Loader */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 3,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(30, 139, 175, 0.3)",
                borderRadius: "4px",
              },
            }}
          >
            {isLoadingUnassignFolders ? (
              // Loader while fetching folders
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#ef4444" }} size={48} />
              </Box>
            ) : filteredAssignedFolders.length > 0 ? (
              <Stack spacing={1.5}>
                {filteredAssignedFolders.map((folder) => (
                  <Box
                    key={folder.id}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      backgroundColor:
                        mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(30, 139, 175, 0.05)",
                      border:
                        mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.1)"
                          : "1px solid rgba(30, 139, 175, 0.1)",
                      mb: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Checkbox
                        checked={!!selectedUnassignFolders[folder.id]}
                        onChange={() => {
                          setSelectedUnassignFolders((prev) => ({
                            ...prev,
                            [folder.id]: !prev[folder.id],
                          }));
                        }}
                        sx={{
                          color: folder.accessType === "free" ? "#1e8bafff" : "#ef4444",
                          "&.Mui-checked": {
                            color: folder.accessType === "free" ? "#1e8bafff" : "#ef4444",
                          },
                        }}
                      />
                      <FolderOpenIcon
                        sx={{
                          color: folder.accessType === "free" ? "#1e8bafff" : "#ef4444",
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          color:
                            mode === "dark"
                              ? palette.dark.text
                              : mode === "custom"
                                ? customColors?.text || "#111111"
                                : folder.accessType === "free" ? "#1e3a8a" : "#b91c1c",
                        }}
                      >
                        {folder.name}
                      </Typography>
                      <Chip
                        label={folder.accessType}
                        size="small"
                        sx={{
                          backgroundColor: folder.accessType === "free" ? "rgba(30, 139, 175, 0.2)" : "rgba(239, 68, 68, 0.2)",
                          color: folder.accessType === "free" ? "#1e8bafff" : "#ef4444",
                          fontWeight: 600,
                          ml: 1,
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  opacity: 0.7,
                }}
              >
                <FolderIcon
                  sx={{ fontSize: 64, color: "#ef4444", mb: 2, opacity: 0.5 }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: mode === "dark" ? palette.dark.text : "#37474f",
                    fontWeight: 500,
                  }}
                >
                  {unassignSearchTerm || filterType !== "all" ? "No matching folders found" : "No folders assigned"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      mode === "dark"
                        ? "rgba(255, 255, 255, 0.5)"
                        : "rgba(0, 0, 0, 0.5)",
                    mt: 1,
                  }}
                >
                  {unassignSearchTerm || filterType !== "all" ? "Try adjusting your search or filter" : "No folders are currently assigned to this class"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Sticky Footer */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              background:
                mode === "dark"
                  ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                  : mode === "custom"
                    ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                    : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
              p: 2,
              borderRadius: "0 0 20px 20px",
              boxShadow:
                mode === "dark"
                  ? "0 -4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 -4px 12px rgba(30, 139, 175, 0.1)",
              borderTop:
                mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(30, 139, 175, 0.1)",
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  color:
                    mode === "dark"
                      ? "rgba(255, 255, 255, 0.7)"
                      : "rgba(0, 0, 0, 0.7)",
                  fontWeight: 500,
                }}
              >
                {Object.values(selectedUnassignFolders).filter(Boolean).length} folder(s) selected
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  onClick={handleCloseUnassignModal}
                  sx={{
                    color: mode === "dark" ? "#ffffff" : "#1e3a8a",
                    borderColor:
                      mode === "dark"
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(30, 139, 175, 0.3)",
                    textTransform: "none",
                    borderRadius: "12px",
                    fontWeight: 600,
                    px: 3,
                    py: 1.25,
                    "&:hover": {
                      borderColor:
                        mode === "dark"
                          ? "rgba(255, 255, 255, 0.5)"
                          : "rgba(30, 139, 175, 0.5)",
                      backgroundColor:
                        mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(30, 139, 175, 0.05)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUnassignFolders}
                  variant="contained"
                  disabled={Object.values(selectedUnassignFolders).filter(Boolean).length === 0}
                  sx={{
                    background: "linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    px: 3,
                    py: 1.25,
                    borderRadius: "12px",
                    boxShadow: "0 4px 14px rgba(185, 28, 28, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #991b1b 0%, #dc2626 100%)",
                      boxShadow: "0 6px 20px rgba(185, 28, 28, 0.4)",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0px)",
                      boxShadow: "0 2px 8px rgba(185, 28, 28, 0.3)",
                    },
                    "&:disabled": {
                      background: "rgba(128, 128, 128, 0.3)",
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  }}
                >
                  Unassign Folders
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </StyledPaper>
  );
}